import { computed } from "mobx"
import { fbAuth, fbFunctions } from "../../common/FirebaseConfig"
import { log } from "../../common/Logging"
import { Month, TimeUtils } from "../../common/TimeUtils"
import { Student } from "../../shared/models/Student"
import { ActivityProgressStoreSA } from "../activity-progress/ActivityProgressStoreSA"
import { ClassStoreSA } from "../classes/ClassStoreSA"
import { StudentResultsStore } from "../students/StudentResultsStore"
import { StudentStoreSA } from "../students/StudentStoreSA"
import { StudentAccountStore } from "./StudentAccountStore"

export class AuthStoreSA {

    private static innerInstance: AuthStoreSA

    static get instance() {
        return this.innerInstance || (this.innerInstance = new this())
    }

    createStudentAccount = async () => {
        log.debug("Begin student account creation")
        const {classId, screenname, studentAccount: {firstName, lastName, studentId, birthDay, birthMonth}, gender} = StudentAccountStore.instance

        const birthCode = TimeUtils.birthMonthAndDayToBirthCode(birthMonth as Month, birthDay as number)

        await fbFunctions().httpsCallable("createStudentAccount")({
            classId,
            screenname,
            firstName,
            lastName,
            studentId: studentId.toLowerCase(),
            birthCode,
            gender,
        })

        log.debug("student account creation complete, begin login")

        try {
            const authTokenResult = await fbFunctions().httpsCallable("getStudentAuthToken")({studentId, birthCode})
            const authToken = authTokenResult.data

            await fbAuth().signInWithCustomToken(authToken)
            log.debug(`logged in student ${studentId}-${birthCode}`)
        } catch (e) {
            log.error(`Couldn't login student! ${studentId}-${birthCode}`)
        }

        await this.loginStudent(studentId, birthCode)
    }

    /**
     * We have to find:
     *
     * 1. the student (wait for student.id and classId used by below, which can all be executed async)
     * 2. listen for student changes
     * 3. listen for activity progress changes
     * 4. listen for student result changes
     */
    loginStudent = async (studentSchoolId: string, birthCode: string) => {

        const lowerCasedId = studentSchoolId.toLowerCase()

        log.debug(`Find student with ${lowerCasedId} / ${birthCode}`)

        const student = await this.findStudent(lowerCasedId, birthCode)

        if (student != null) {

            StudentStoreSA.instance.student = student
            const listenStudentChangesPromise = StudentStoreSA.instance.listenForStudentChanges(student.id)
            const listenForActivityProgressPromise = ActivityProgressStoreSA.instance.listenForActivityProgress(student.id)

            await StudentResultsStore.instance.listenForStudentResultChanges(student.classId)
            await listenStudentChangesPromise
            await listenForActivityProgressPromise

            await ClassStoreSA.instance.loadClass(student.classId)

        }

        return student
    }

    findStudent = async (studentId: string, birthCode: string): Promise<Student | undefined> => {
        const result = await fbFunctions().httpsCallable("findStudentWithIdAndBirthCode")({studentId, birthCode})
        return result.data as Student | undefined
    }

    @computed
    get studentIsFullyLoaded(): boolean {
        return ClassStoreSA.instance.clazz != null
    }
}
